import { useEffect, useImperativeHandle } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";

import { Translate } from "../../../../../hooks/useTranslation";
import { type FlowType } from "../../FlowStepper";
import CollapsibleContainer from "./CollapsibleContainer";
import { SelectDocumentsContextProvider } from "./context";
import { TimingSelectorWithContext } from "./TimingSelector";

interface Props {
    flowType: FlowType;
    controlRef?: React.RefObject<{ submit?: () => Promise<void> }>;
}

export const SelectDocuments = ({ controlRef, flowType }: Props) => {
    const navigate = useNavigate();
    const { pageLoadEvent } = useYardActionEvents();
    const user = useFCSubscriptionStore((state) => state.user);
    const stepDataSelectDocs = useFCSubscriptionStore(
        (state) => state.flowSteps["select-documents"],
    );
    const stepDataAddRecipient = useFCSubscriptionStore(
        (state) => state.flowSteps["add-recipient"],
    );
    const subscribeTo = useFCSubscriptionStore((state) => state.subscribeTo);
    const shareSubscriptions = useFCSubscriptionStore(
        (state) => state.shareSubscriptions,
    );

    const showTimingSelector =
        stepDataSelectDocs.data &&
        stepDataSelectDocs.data.subscriptions?.some(
            (sub) => sub.frequency !== "DAILY",
        );

    const userId = user?.userId;

    const { mutateAsync } = useMutation({
        mutationFn: async () => {
            if (!stepDataSelectDocs.complete)
                throw new Error("Step is not complete");
            if (
                !stepDataSelectDocs.data ||
                !stepDataSelectDocs.data.subscriptions
            )
                throw new Error("Step data is not available");
            const { subscriptions, watchlist = [] } = stepDataSelectDocs.data;
            if (flowType !== "share") {
                await subscribeTo(subscriptions.concat(watchlist));
            } else {
                const emails = stepDataAddRecipient.data?.emails || [];
                if (emails.length === 0)
                    throw new Error("Please enter some emails");
                await shareSubscriptions(
                    emails.map((email) => ({
                        recipientEmail: email,
                        subscriptions,
                        message: stepDataAddRecipient.data?.message,
                    })),
                );
            }
        },
        onSuccess: () => {
            navigate("/my-funds");
        },
    });

    useImperativeHandle(
        controlRef,
        () =>
            userId
                ? {
                      async submit() {
                          await mutateAsync();
                      },
                  }
                : {},
        [userId, mutateAsync],
    );

    useEffect(() => {
        setTimeout(() => pageLoadEvent({ pageName: "Select documents" }), 50);
    }, []);

    return (
        <SelectDocumentsContextProvider type="FULL">
            <h2 className="my-2 font-medium">
                <Translate>Select documents</Translate>
            </h2>
            <p className="my-2 max-sm:text-sm">
                <Translate>
                    You will receive email notifications when these documents
                    are published or updated
                </Translate>
                .
            </p>
            <CollapsibleContainer />
            {showTimingSelector && <TimingSelectorWithContext />}
        </SelectDocumentsContextProvider>
    );
};
